import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import {
  listeZuText,
  mergeOptionen,
  splitListe,
  vkBruttoCents,
  WERT_OHNE_KOMMA,
  type Hersteller,
  type JabStatus,
  type StoffIntern,
  type StoffWerteArt,
} from "@repo/shared";

import { BildAuswahl } from "../components/BildAuswahl";
import { MehrfachAuswahl } from "../components/stoffe/MehrfachAuswahl";
import { euroText, faktorText } from "../components/stoffe/StoffZellen";
import { api, errorMessage } from "../lib/api";
import {
  abgleichBreite,
  FARBABGLEICH,
  LIEFERANT_NAME,
  lieferantVon,
  type FarbStand,
  type FarbVerfuegbarkeit,
} from "../lib/farbabgleich";

const PFLICHT = "Pflichtfeld.";

const JAB_STATUS_TEXT: Record<JabStatus, string> = {
  verfuegbar: "verfügbar",
  knapp: "knapp",
  nicht_verfuegbar: "nicht verfügbar",
  unbekannt: "unbekannt",
};

/** ISO-Datum „JJJJ-MM-TT“ → „TT.MM.JJJJ“; anderes bleibt, wie es ist. */
function datumText(iso: string): string {
  const t = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return t ? `${t[3]}.${t[2]}.${t[1]}` : iso;
}

/** „12,5 m (verfügbar)“, mit angekündigtem Zugang z. B. „0 m (nicht verfügbar) · erwartet 40 m am 15.11.2026“. */
function meterText(v: FarbVerfuegbarkeit): string {
  const menge = v.meter === null ? "Menge unbekannt" : `${v.meter.toLocaleString("de-DE")} m`;
  const text = `${menge} (${JAB_STATUS_TEXT[v.status]})`;
  if (!v.erwartet) return text;
  const termin = v.erwartet.termin ? ` am ${datumText(v.erwartet.termin)}` : "";
  return `${text} · erwartet ${v.erwartet.meter.toLocaleString("de-DE")} m${termin}`;
}

type Formular = {
  name: string;
  herstellerId: string;
  nummer: string;
  ballenbreiteMm: string;
  rapportMm: string;
  typ: string[];
  material: string[];
  /** Euro mit Komma, z. B. „29,99“. */
  ek: string;
  faktor: string;
  bildUrl: string;
  lagerort: string;
  schwerEntflammbar: boolean;
  recyclebar: boolean;
  notiz: string;
  aktiv: boolean;
  // Angaben, die die Stoff-Konfiguratoren nutzen
  farbe: string;
  farbnummer: string;
  kollektion: string;
  querVerarbeitbar: boolean;
};

type Feldfehler = Partial<Record<keyof Formular, string>>;
type TextSchluessel =
  | "name"
  | "nummer"
  | "ballenbreiteMm"
  | "rapportMm"
  | "ek"
  | "faktor"
  | "bildUrl"
  | "lagerort"
  | "farbe"
  | "farbnummer"
  | "kollektion";
type Haekchen = "schwerEntflammbar" | "recyclebar" | "aktiv" | "querVerarbeitbar";

// Wie die Neuanlage im ERP: nur Rapport 0 und „aktiv“ sind vorbelegt.
const LEER: Formular = {
  name: "",
  herstellerId: "",
  nummer: "",
  ballenbreiteMm: "",
  rapportMm: "0",
  typ: [],
  material: [],
  ek: "",
  faktor: "",
  bildUrl: "",
  lagerort: "",
  schwerEntflammbar: false,
  recyclebar: false,
  notiz: "",
  aktiv: true,
  farbe: "",
  farbnummer: "",
  kollektion: "",
  querVerarbeitbar: false,
};

function formularAus(s: StoffIntern): Formular {
  return {
    name: s.name,
    herstellerId: s.herstellerId,
    nummer: s.nummer,
    ballenbreiteMm: String(s.ballenbreiteMm),
    rapportMm: String(s.rapportMm),
    typ: splitListe(s.typ),
    material: splitListe(s.material),
    // 0 Cent heißt „kein EK“ – die Liste zeigt dann „—“, das Formular ein leeres Feld.
    ek: s.ekProLfmCents > 0 ? (s.ekProLfmCents / 100).toFixed(2).replace(".", ",") : "",
    faktor: faktorText(s.faktor),
    bildUrl: s.bildUrl ?? "",
    lagerort: s.lagerort ?? "",
    schwerEntflammbar: s.schwerEntflammbar,
    recyclebar: s.recyclebar,
    notiz: s.notiz ?? "",
    aktiv: s.aktiv,
    farbe: s.farbe ?? "",
    farbnummer: s.farbnummer ?? "",
    kollektion: s.kollektion ?? "",
    querVerarbeitbar: s.querVerarbeitbar,
  };
}

/** „29,99“, „29.99“ oder „1.234,50“ → Zahl; leer → null; alles andere (auch Minus) → NaN. */
function dezimal(text: string): number | null {
  const t = text.replace(/\s/g, "");
  if (!t) return null;
  const norm = t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t;
  return /^\d+(\.\d+)?$/.test(norm) ? Number(norm) : Number.NaN;
}

/** Ganze Millimeter; leer → null; alles andere → NaN. */
function ganzzahl(text: string): number | null {
  const t = text.replace(/\s/g, "");
  if (!t) return null;
  return /^\d+$/.test(t) ? Number(t) : Number.NaN;
}

/** Nur http(s) – die Liste zeigt die Adresse als Bild oder Link. */
function istWebAdresse(text: string): boolean {
  try {
    const url = new URL(text);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/** Prüft im Browser, was die API ablehnen würde – mit dem Fehler direkt unter dem Feld. */
function pruefe(f: Formular, istNeu: boolean): Feldfehler {
  const fehler: Feldfehler = {};
  if (!f.name.trim()) fehler.name = PFLICHT;
  if (istNeu && !f.herstellerId) fehler.herstellerId = PFLICHT;

  const breite = ganzzahl(f.ballenbreiteMm);
  if (breite === null) fehler.ballenbreiteMm = PFLICHT;
  else if (!(breite >= 1 && breite <= 10_000)) fehler.ballenbreiteMm = "Ganze Millimeter von 1 bis 10000.";

  const rapport = ganzzahl(f.rapportMm);
  if (rapport !== null && !(rapport >= 0 && rapport <= 10_000)) {
    fehler.rapportMm = "Ganze Millimeter von 0 bis 10000.";
  }

  const ek = dezimal(f.ek);
  if (ek !== null && !(ek >= 0)) fehler.ek = "Betrag ab 0, z. B. 29,99.";
  else if (ek !== null && ek > 1_000_000) fehler.ek = "Höchstens 1.000.000 €.";

  const faktor = dezimal(f.faktor);
  if (faktor !== null && !(faktor >= 0 && faktor <= 999)) fehler.faktor = "Zahl von 0 bis 999, z. B. 2,90.";

  if (f.bildUrl.trim() && !istWebAdresse(f.bildUrl.trim())) {
    fehler.bildUrl = "Vollständige Adresse mit http:// oder https://.";
  }
  if ((listeZuText(f.typ) ?? "").length > 200) fehler.typ = "Zu viele Werte – höchstens 200 Zeichen.";
  if ((listeZuText(f.material) ?? "").length > 200) fehler.material = "Zu viele Werte – höchstens 200 Zeichen.";
  return fehler;
}

/** Leere Texte werden zu null – sonst stünde "" in der Datenbank. */
const textOderNull = (wert: string) => (wert.trim() === "" ? null : wert.trim());

/** Listen-Zustand („?…“), mit dem die Liste hierher verlinkt; ohne ihn geht es zur Standardansicht. */
function listenQuery(state: unknown): string {
  const liste = (state as { liste?: unknown } | null)?.liste;
  return typeof liste === "string" && (liste === "" || liste.startsWith("?")) ? liste : "";
}

const fehlerText = (cause: unknown, ersatz: string) => (cause instanceof Error ? cause.message : ersatz);

/** Formular „Stoff bearbeiten“ (`/stoffe/:id`) bzw. „Neuer Stoff“ (`/stoffe/neu`) nach dem Vorbild des früheren ERP. */
export function StoffDetailPage() {
  const { id } = useParams();
  // Wechselt die Id, beginnt ein frisches Formular – sonst stünden Werte des vorigen Stoffs darin.
  return <StoffFormular key={id ?? "neu"} id={id} />;
}

function StoffFormular({ id }: { id: string | undefined }) {
  const istNeu = id === undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const liste = listenQuery(location.state);
  const zurListe = { pathname: "/stoffe", search: liste };
  const ids = useId();
  const idVon = (k: keyof Formular) => `${ids}-${k}`;

  const [form, setForm] = useState<Formular>(LEER);
  const [titelName, setTitelName] = useState("");
  const [herstellerName, setHerstellerName] = useState("");
  const [hersteller, setHersteller] = useState<Hersteller[]>([]);
  const [werte, setWerte] = useState<Record<StoffWerteArt, string[]>>({ typ: [], material: [] });
  const [laedt, setLaedt] = useState(!istNeu);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [feldfehler, setFeldfehler] = useState<Feldfehler>({});
  const [pruefungen, setPruefungen] = useState(0);
  const [speichert, setSpeichert] = useState(false);
  const [stoffAufschlag, setStoffAufschlag] = useState<number | null>(null);
  // Farbabgleich (JAB, Sarlas, ED): Nach dem Abgleich wird „Farbnummer“ zur Auswahl aus den Farben des Lieferanten.
  const [farben, setFarben] = useState<string[]>([]);
  // Farbnummer → Bild-URL; fehlt für Farben, zu denen der Lieferant kein Bild liefert.
  // null = nie mit Bildern abgeglichen; {} = abgeglichen, der Lieferant liefert keine Bilder.
  const [farbbilder, setFarbbilder] = useState<Record<string, string> | null>(null);
  // Farbnummer → Farbname (nur Sarlas); erscheint als Zusatz in der Auswahl.
  const [farbnamen, setFarbnamen] = useState<Record<string, string>>({});
  // Ein Abgleich bei gespeicherten Stoffen landet sofort in der DB; offen bleibt er nur bei neuen Stoffen.
  const [farbenOffen, setFarbenOffen] = useState(false);
  const [abgleichHinweis, setAbgleichHinweis] = useState<string | null>(null);
  const [treffer, setTreffer] = useState<string | null>(null);
  const [meter, setMeter] = useState("");
  const [abgleichLaedt, setAbgleichLaedt] = useState(false);
  const [verfLaedt, setVerfLaedt] = useState(false);
  const [farbFehler, setFarbFehler] = useState<string | null>(null);
  // Vom Abgleich vorgeschlagene Stoffnummer, wenn über den Namen gefunden (Sarlas: Tirella-Nr., ED: Produktcode).
  const [vorschlagNummer, setVorschlagNummer] = useState<string | null>(null);
  // Der geladene Stoff: Seinen Farbstand gibt es erst, wenn auch die Herstellerliste (und damit der Lieferant) da ist.
  const [geladenerStoff, setGeladenerStoff] = useState<StoffIntern | null>(null);
  const standGeladen = useRef(false);
  // Zählt Lieferantenwechsel: Antworten, die danach ankommen, gehören zum alten Lieferanten und werden verworfen.
  const abgleichLauf = useRef(0);

  useEffect(() => {
    api.api.admin.einstellungen
      .$get()
      .then(async (res) => {
        if (res.ok) setStoffAufschlag((await res.json()).stoffAufschlagProzent);
      })
      .catch(() => {});
  }, []);
  // Ist die Neuanlage durch, das Abwählen von „aktiv“ aber gescheitert, ändert ein zweiter Versuch diesen Stoff.
  const angelegtId = useRef<string | null>(null);

  /* --- Laden ----------------------------------------------------------------------------- */

  useEffect(() => {
    let aktuell = true;
    api.api.admin.hersteller
      .$get()
      .then(async (res) => {
        if (!res.ok) throw new Error(await errorMessage(res, "Hersteller nicht ladbar"));
        const d = await res.json();
        if (aktuell && "hersteller" in d) setHersteller(d.hersteller);
      })
      .catch((cause: unknown) => {
        if (aktuell) setFehler(fehlerText(cause, "Hersteller nicht ladbar"));
      });
    return () => {
      aktuell = false;
    };
  }, []);

  const ladeWerte = useCallback(async (art: StoffWerteArt): Promise<string[]> => {
    const res = await api.api.admin["stoff-werte"][":art"].$get({ param: { art } });
    if (!res.ok) throw new Error(await errorMessage(res, "Werte nicht ladbar"));
    const d = await res.json();
    const namen = "werte" in d ? d.werte.map((w) => w.name) : [];
    setWerte((alt) => ({ ...alt, [art]: namen }));
    return namen;
  }, []);

  useEffect(() => {
    Promise.all([ladeWerte("typ"), ladeWerte("material")]).catch((cause: unknown) =>
      setFehler(fehlerText(cause, "Werte nicht ladbar")),
    );
  }, [ladeWerte]);

  useEffect(() => {
    if (!id) return;
    let aktuell = true;
    api.api.admin.stoffe[":id"]
      .$get({ param: { id } })
      .then(async (res) => {
        const status: number = res.status;
        // 400 heißt hier: keine gültige Id in der Adresse – für den Nutzer dasselbe wie „nicht gefunden“.
        if (status === 400 || status === 404) throw new Error("Stoff nicht gefunden.");
        if (!res.ok) throw new Error(await errorMessage(res, "Stoff nicht ladbar"));
        const d = await res.json();
        if (!aktuell || !("stoff" in d)) return;
        setForm(formularAus(d.stoff));
        setGeladenerStoff(d.stoff);
        setTitelName(d.stoff.name);
        setHerstellerName(d.stoff.herstellerName);
      })
      .catch((cause: unknown) => {
        if (aktuell) setLadeFehler(fehlerText(cause, "Stoff nicht ladbar"));
      })
      .finally(() => {
        if (aktuell) setLaedt(false);
      });
    return () => {
      aktuell = false;
    };
  }, [id]);

  // Gespeicherter Farbstand – einmal, sobald Stoff und Herstellerliste geladen sind. Vor dem Zeichnen,
  // damit „Farbnummer“ nicht kurz als Freitext erscheint, bevor die Auswahl mit den Farben kommt.
  useLayoutEffect(() => {
    if (!geladenerStoff || hersteller.length === 0 || standGeladen.current) return;
    standGeladen.current = true;
    const schluessel = hersteller.find((h) => h.id === geladenerStoff.herstellerId)?.schluessel ?? "";
    const l = lieferantVon(schluessel);
    if (!l) return;
    const stand = FARBABGLEICH[l].ausStoff(geladenerStoff);
    setFarben(stand.farben);
    setFarbbilder(stand.bilder);
    setFarbnamen(stand.namen);
  }, [geladenerStoff, hersteller]);

  /** Neuer Typ bzw. neues Material aus der Auswahl; bei 409 gilt der vorhandene Name. */
  const neuerWert = useMemo(() => {
    const fuer =
      (art: StoffWerteArt) =>
      async (name: string): Promise<string | null> => {
        if (name.includes(",")) {
          setFehler(WERT_OHNE_KOMMA);
          return null;
        }
        try {
          const res = await api.api.admin["stoff-werte"][":art"].$post({ param: { art }, json: { name } });
          if (res.ok) {
            const d = await res.json();
            if ("wert" in d) {
              setWerte((alt) => ({ ...alt, [art]: mergeOptionen(alt[art], [d.wert.name]) }));
              return d.wert.name;
            }
          }
          if (res.status === 409) {
            const vorhanden = (await ladeWerte(art)).find((n) => n.toLowerCase() === name.trim().toLowerCase());
            if (vorhanden) return vorhanden;
          }
          setFehler(await errorMessage(res, "Wert konnte nicht angelegt werden"));
        } catch {
          setFehler("Wert konnte nicht angelegt werden");
        }
        return null;
      };
    return { typ: fuer("typ"), material: fuer("material") };
  }, [ladeWerte]);

  /* --- Bearbeiten und Speichern ---------------------------------------------------------- */

  function setze<K extends keyof Formular>(k: K, wert: Formular[K]) {
    setForm((f) => ({ ...f, [k]: wert }));
    // Wer ein Feld korrigiert, sieht dessen alten Fehler nicht mehr.
    setFeldfehler((alt) => {
      if (!alt[k]) return alt;
      const rest = { ...alt };
      delete rest[k];
      return rest;
    });
  }

  const herstellerSchluessel = hersteller.find((h) => h.id === form.herstellerId)?.schluessel ?? "";
  const lieferant = lieferantVon(herstellerSchluessel);

  function farbenZuruecksetzen() {
    // Laufende Anfragen gehören zum alten Lieferanten – ihre Antworten werden verworfen.
    abgleichLauf.current += 1;
    setFarben([]);
    setFarbbilder(null);
    setFarbnamen({});
    setFarbenOffen(false);
    setAbgleichHinweis(null);
    setTreffer(null);
    setMeter("");
    setFarbFehler(null);
    setVorschlagNummer(null);
    setAbgleichLaedt(false);
    setVerfLaedt(false);
  }

  function herstellerWechseln(neueId: string) {
    setze("herstellerId", neueId);
    setVorschlagNummer(null);
    const schluessel = hersteller.find((h) => h.id === neueId)?.schluessel ?? "";
    // Auch zwischen JAB, Sarlas und ED: Farben des einen Lieferanten gehören nie in die Felder eines anderen.
    if (lieferantVon(schluessel) !== lieferant) farbenZuruecksetzen();
  }

  /** Stoffnummer von Hand geändert: Ein Vorschlag aus dem Abgleich gilt dann nicht mehr. */
  function nummerAendern(nummer: string) {
    setze("nummer", nummer);
    setVorschlagNummer(null);
  }

  async function farbenAbgleichen() {
    if (!lieferant) return;
    const lauf = abgleichLauf.current;
    setFarbFehler(null);
    setAbgleichHinweis(null);
    setMeter("");
    setVorschlagNummer(null);
    setAbgleichLaedt(true);
    try {
      // Mit der Id speichert der Server die Farben sofort am Stoff – auch ohne „Speichern“.
      const stoffId = id ?? angelegtId.current ?? undefined;
      const d = await FARBABGLEICH[lieferant].abgleichen({
        herstellerSchluessel,
        name: form.name,
        nummer: form.nummer,
        // Nur ED nutzt die Ballenbreite; ohne gültige Angabe entfällt sie.
        breiteMm: abgleichBreite(ganzzahl(form.ballenbreiteMm)),
        stoffId,
      });
      if (lauf !== abgleichLauf.current) return;
      setFarben(d.farben);
      setFarbbilder(d.bilder);
      setFarbnamen(d.namen);
      setFarbenOffen(!d.gespeichert);
      setAbgleichHinweis(
        d.gespeichert
          ? `${d.farben.length} Farben am Stoff gespeichert`
          : `${d.farben.length} Farben – werden mit dem Stoff gespeichert`,
      );
      setTreffer(d.treffer);
      setVorschlagNummer(d.vorschlagNummer);
    } catch (cause) {
      if (lauf === abgleichLauf.current) setFarbFehler(fehlerText(cause, "Abgleich fehlgeschlagen"));
    } finally {
      if (lauf === abgleichLauf.current) setAbgleichLaedt(false);
    }
  }

  async function farbVerfuegbarkeit() {
    if (!form.farbnummer || !lieferant) return;
    const lauf = abgleichLauf.current;
    setFarbFehler(null);
    setMeter("");
    setVerfLaedt(true);
    try {
      const v = await FARBABGLEICH[lieferant].verfuegbarkeit({
        herstellerSchluessel,
        name: form.name,
        nummer: form.nummer,
        treffer,
        farbnummer: form.farbnummer,
        // Ein neu ermittelter Treffer bleibt, auch wenn die Verfügbarkeit danach scheitert.
        trefferErmittelt: (t) => {
          if (lauf === abgleichLauf.current) setTreffer(t);
        },
      });
      if (lauf !== abgleichLauf.current) return;
      setMeter(meterText(v));
    } catch (cause) {
      if (lauf === abgleichLauf.current) setFarbFehler(fehlerText(cause, "Verfügbarkeit gerade nicht abrufbar"));
    } finally {
      if (lauf === abgleichLauf.current) setVerfLaedt(false);
    }
  }

  // noValidate schaltet den Sprung des Browsers ab: nach dem Prüfen zum ersten fehlerhaften Feld.
  useEffect(() => {
    if (pruefungen === 0) return;
    const erstes = document.querySelector<HTMLElement>(".stoff-formular [aria-invalid='true']");
    erstes?.focus({ preventScroll: true });
    erstes?.scrollIntoView({ block: "center" });
  }, [pruefungen]);

  async function speichern(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (speichert) return;
    setFehler(null);
    const geprueft = pruefe(form, istNeu);
    setFeldfehler(geprueft);
    if (Object.keys(geprueft).length > 0) {
      setPruefungen((n) => n + 1);
      return;
    }
    const stand: FarbStand = { farben, bilder: farbbilder, namen: farbnamen };

    const daten = {
      name: form.name.trim(),
      nummer: form.nummer.trim(),
      ballenbreiteMm: ganzzahl(form.ballenbreiteMm) ?? 0,
      rapportMm: ganzzahl(form.rapportMm) ?? 0,
      typ: listeZuText(form.typ),
      material: listeZuText(form.material),
      ekProLfmCents: Math.round((dezimal(form.ek) ?? 0) * 100),
      // Leer gilt der Standardfaktor 1 – wie bei der Anlage über die API.
      faktor: dezimal(form.faktor) ?? 1,
      bildUrl: textOderNull(form.bildUrl),
      lagerort: textOderNull(form.lagerort),
      schwerEntflammbar: form.schwerEntflammbar,
      recyclebar: form.recyclebar,
      notiz: textOderNull(form.notiz),
      farbe: textOderNull(form.farbe),
      farbnummer: textOderNull(form.farbnummer),
      kollektion: textOderNull(form.kollektion),
      // Lichtdurchlässigkeit und Preisgruppe pflegt die Maske nicht mehr: nicht senden, damit
      // vorhandene Werte erhalten bleiben (neue Stoffe bekommen den Standard der Datenbank).
      querVerarbeitbar: form.querVerarbeitbar,
      // Nur einen noch nicht gespeicherten Abgleich mitsenden – sonst stünde jab-, sarlas- bzw. englischAbgeglichenAm
      // nach jedem Speichern auf „jetzt“. Den Zeitpunkt setzt der Server.
      ...(farbenOffen && farben.length > 0 && lieferant ? FARBABGLEICH[lieferant].speicherFelder(stand) : {}),
    };

    setSpeichert(true);
    try {
      let ziel = id ?? angelegtId.current;
      if (!ziel) {
        const res = await api.api.admin.stoffe.$post({ json: { ...daten, herstellerId: form.herstellerId } });
        if (!res.ok) throw new Error(await errorMessage(res, "Anlegen fehlgeschlagen"));
        const d = await res.json();
        if (!("stoff" in d)) throw new Error("Anlegen fehlgeschlagen");
        ziel = angelegtId.current = d.stoff.id;
        // Die Anlage kennt kein „aktiv“ – ein neuer Stoff ist aktiv. Abgewählt wird gleich danach.
        if (form.aktiv) {
          navigate(zurListe, { replace: true });
          return;
        }
      }
      const res = await api.api.admin.stoffe[":id"].$patch({ param: { id: ziel }, json: { ...daten, herstellerId: form.herstellerId || undefined, aktiv: form.aktiv } });
      if (!res.ok) throw new Error(await errorMessage(res, "Speichern fehlgeschlagen"));
      navigate(zurListe, { replace: true });
    } catch (cause) {
      setFehler(fehlerText(cause, "Speichern fehlgeschlagen"));
    } finally {
      setSpeichert(false);
    }
  }

  /* --- Ansicht --------------------------------------------------------------------------- */

  const kopfLink = (
    <p className="breadcrumb">
      <Link to={zurListe}>← Stoffe</Link>
    </p>
  );

  if (laedt) return <p className="muted">Lade Stoff …</p>;
  if (ladeFehler) {
    return (
      <section>
        {kopfLink}
        <p className="error">{ladeFehler}</p>
      </section>
    );
  }

  const ekWert = dezimal(form.ek);
  const faktorWert = dezimal(form.faktor);
  const vk =
    ekWert !== null && faktorWert !== null && Number.isFinite(ekWert) && Number.isFinite(faktorWert)
      ? vkBruttoCents(Math.round(ekWert * 100), faktorWert)
      : null;
  const herstellerFehlt = !istNeu && form.herstellerId !== "" && !hersteller.some((h) => h.id === form.herstellerId);

  const ekFiliale =
    ekWert !== null && Number.isFinite(ekWert) && stoffAufschlag !== null
      ? Math.round(Math.round(ekWert * 100) * (1 + stoffAufschlag / 100))
      : null;

  const feld = (k: keyof Formular, label: string, eingabe: ReactNode, hinweis?: ReactNode) => (
    <div className="field">
      <label htmlFor={idVon(k)}>{label}</label>
      {eingabe}
      {feldfehler[k] && (
        <small className="error" id={`${idVon(k)}-fehler`}>
          {feldfehler[k]}
        </small>
      )}
      {hinweis && <small>{hinweis}</small>}
    </div>
  );

  const fehlerAttribute = (k: keyof Formular) =>
    feldfehler[k] ? { "aria-invalid": true, "aria-describedby": `${idVon(k)}-fehler` } : {};

  const textfeld = (k: TextSchluessel, extra: InputHTMLAttributes<HTMLInputElement> = {}) => (
    <input
      id={idVon(k)}
      type="text"
      className="stoffe-feld stoffe-feld--gefuellt"
      value={form[k]}
      onChange={(e) => setze(k, e.target.value)}
      {...fehlerAttribute(k)}
      {...extra}
    />
  );

  const haekchen = (k: Haekchen, label: string) => (
    <label className="field field--inline">
      <input type="checkbox" checked={form[k]} onChange={(e) => setze(k, e.target.checked)} />
      {label}
    </label>
  );

  // Bei JAB-, Sarlas- und ED-Stoffen mit abgeglichenen Farben ist „Farbnummer“ eine Auswahl mit Stoffbild; sonst Freitext.
  // Eine gespeicherte Nummer, die der Lieferant nicht (mehr) führt, bleibt wählbar statt still zu verschwinden.
  const farbnummerEingabe =
    lieferant !== null && farben.length > 0 ? (
      <BildAuswahl
        id={idVon("farbnummer")}
        {...fehlerAttribute("farbnummer")}
        label="Farbnummer"
        platzhalter="— bitte wählen —"
        leerOption="— bitte wählen —"
        wert={form.farbnummer}
        optionen={[
          ...(form.farbnummer && !farben.includes(form.farbnummer)
            ? [{ wert: form.farbnummer, label: form.farbnummer, zusatz: `nicht bei ${LIEFERANT_NAME[lieferant]}` }]
            : []),
          ...farben.map((f) => ({ wert: f, label: f, bildUrl: farbbilder?.[f] ?? null, zusatz: farbnamen[f] ?? null })),
        ]}
        onChange={(wert) => {
          setze("farbnummer", wert);
          setMeter("");
        }}
      />
    ) : (
      textfeld("farbnummer", { maxLength: 64 })
    );

  return (
    <section>
      {kopfLink}
      <h1>{istNeu ? "Neuer Stoff" : "Stoff bearbeiten"}</h1>
      <p className="muted">{istNeu ? "Dekostoff anlegen." : titelName}</p>

      <form className="stoff-formular" onSubmit={speichern} noValidate>
        {feld("name", "Stoffname *", textfeld("name", { maxLength: 255 }))}
        {feld(
          "herstellerId",
          "Hersteller *",
          <select
            id={idVon("herstellerId")}
            value={form.herstellerId}
            onChange={(e) => herstellerWechseln(e.target.value)}
            {...fehlerAttribute("herstellerId")}
          >
            {istNeu && <option value="">— bitte wählen —</option>}
            {herstellerFehlt && <option value={form.herstellerId}>{herstellerName}</option>}
            {hersteller.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>,
        )}

        <div className="stoff-formular__raster">
          {feld(
            "nummer",
            "Stoffnummer",
            textfeld("nummer", { maxLength: 64, onChange: (e) => nummerAendern(e.target.value) }),
            lieferant !== null ? FARBABGLEICH[lieferant].nummerHinweis : null,
          )}
          {feld("ballenbreiteMm", "Ballenbreite (mm) *", textfeld("ballenbreiteMm", { inputMode: "numeric" }))}
          {feld("rapportMm", "Rapport (mm)", textfeld("rapportMm", { inputMode: "numeric" }), "0 = uni.")}
          {feld("kollektion", "Kollektion", textfeld("kollektion", { maxLength: 120 }))}
          {feld("farbe", "Farbe", textfeld("farbe", { maxLength: 120 }))}
          {feld("farbnummer", "Farbnummer", farbnummerEingabe)}
          {lieferant !== null && (
            <div className="field">
              <button type="button" onClick={farbenAbgleichen} disabled={abgleichLaedt}>
                {abgleichLaedt ? "Gleiche ab …" : FARBABGLEICH[lieferant].knopf}
              </button>
              {abgleichHinweis && <small className="muted">{abgleichHinweis}</small>}
              {/* Übernommen wird erst mit „Speichern“, wie jede andere Änderung im Formular. */}
              {vorschlagNummer && vorschlagNummer !== form.nummer.trim() && (
                <button
                  type="button"
                  onClick={() => {
                    setze("nummer", vorschlagNummer);
                    setVorschlagNummer(null);
                  }}
                >
                  {FARBABGLEICH[lieferant].vorschlagText(vorschlagNummer)}
                </button>
              )}
              {/* Nur, wenn nie mit Bildern abgeglichen – liefert der Lieferant keine Bilder ({}), hilft neu Abgleichen nicht. */}
              {farben.length > 0 && farbbilder === null && !abgleichHinweis && (
                <small className="muted">Noch keine Farbbilder – einmal neu abgleichen.</small>
              )}
              {farbFehler && (
                <small className="error" role="alert">
                  {farbFehler}
                </small>
              )}
            </div>
          )}
          {lieferant !== null && farben.length > 0 && (
            <div className="field">
              <label htmlFor={`${ids}-meter`}>Verfügbare Meter</label>
              <input id={`${ids}-meter`} className="stoffe-feld" value={meter} placeholder="—" readOnly />
              <button type="button" onClick={farbVerfuegbarkeit} disabled={!form.farbnummer || verfLaedt}>
                {verfLaedt ? "Prüfe …" : "Farbverfügbarkeit checken"}
              </button>
            </div>
          )}
        </div>

        {haekchen("querVerarbeitbar", "quer verarbeitbar")}

        <div className="stoff-formular__raster">
          {feld(
            "typ",
            "Typ",
            <MehrfachAuswahl
              id={idVon("typ")}
              label="Typ"
              platzhalter="Typ wählen…"
              optionen={werte.typ}
              gewaehlt={form.typ}
              onChange={(typ) => setze("typ", typ)}
              neuerWert={neuerWert.typ}
            />,
            "Werte über „Typen verwalten“ pflegen.",
          )}
          {feld(
            "material",
            "Material",
            <MehrfachAuswahl
              id={idVon("material")}
              label="Material"
              platzhalter="Material wählen…"
              optionen={werte.material}
              gewaehlt={form.material}
              onChange={(material) => setze("material", material)}
              neuerWert={neuerWert.material}
            />,
            "Werte über „Materialien verwalten“ pflegen.",
          )}
          {feld(
            "ek",
            "EK-Preis (€)",
            textfeld("ek", { inputMode: "decimal" }),
            ekFiliale !== null ? (
              <>
                EK Fachhändler (+{stoffAufschlag} %) = <strong>{euroText(ekFiliale)}</strong> netto – geht an die Filialen.
              </>
            ) : undefined,
          )}
          {feld(
            "faktor",
            "Faktor",
            textfeld("faktor", { inputMode: "decimal", placeholder: "1,00" }),
            <>
              VK brutto = EK × Faktor × 1,19.
              {vk !== null && (
                <>
                  {" "}
                  <strong className="stoff-formular__vk">= {euroText(vk)} brutto</strong> – geht als UVP an die Filialen.
                </>
              )}
            </>,
          )}
          {feld("bildUrl", "Bild-URL", textfeld("bildUrl", { inputMode: "url", maxLength: 500 }), "Bild oder PDF.")}
          {feld("lagerort", "Lagerort", textfeld("lagerort", { maxLength: 120 }))}
        </div>

        <div className="stoff-formular__haekchen">
          {haekchen("schwerEntflammbar", "Schwer entflammbar")}
          {haekchen("recyclebar", "Recyclebar")}
        </div>

        {feld(
          "notiz",
          "Notizen",
          <textarea id={idVon("notiz")} rows={3} value={form.notiz} onChange={(e) => setze("notiz", e.target.value)} />,
        )}

        {haekchen("aktiv", "aktiv")}

        {fehler && (
          <p className="error" role="alert">
            {fehler}
          </p>
        )}
        <div>
          <button type="submit" className="primary stoffe-pill" disabled={speichert}>
            Speichern
          </button>
        </div>
      </form>
    </section>
  );
}
